import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, forwardRef, OnDestroy, OnInit } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';

import { ButtonModule } from 'primeng/button';
import { CalendarModule } from 'primeng/calendar';
import { CheckboxModule } from 'primeng/checkbox';
import { DialogModule } from 'primeng/dialog';
import { DropdownModule } from 'primeng/dropdown';
import { FloatLabelModule } from 'primeng/floatlabel';
import { InputTextModule } from 'primeng/inputtext';
import { InputTextareaModule } from 'primeng/inputtextarea';
import { MultiSelectModule } from 'primeng/multiselect';
import { RadioButtonModule } from 'primeng/radiobutton';

import { Course } from '../../../models/course.model';
import { Residence } from '../../../models/residence.model';
import { Role } from '../../../models/role.model';
import { Sponsor } from '../../../models/sponsor.model';
import { user } from '../../../models/user.model';

import { NotificationService } from '../../../services/notification.service';
import { PasswordService } from '../../../services/password.service';
import { SponsorService } from '../../../services/sponsor.service';
import { UserService } from '../../../services/user.service';

import { DynamicDialogConfig } from 'primeng/dynamicdialog';
import { EditorModule } from 'primeng/editor';
import { TooltipModule } from 'primeng/tooltip';
import {
  CAREERS,
  EDUCATION_LEVELS,
  EXAM_TYPES,
  GENDERS,
  LEAD_STATUS,
} from '../../../../constants';
import { environment } from '../../../../environments/environment';
import { CourseService } from '../../../services/course.service';
import { DateTimeService } from '../../../services/date-time.service';
import { DialogManagerService } from '../../../services/dialog-manager.service';
import { ActivityPanelSmallComponent } from '../../activity-panel-small/activity-panel-small.component';
import { ActivityTypeComponent } from '../../activity-type/activity-type.component';

import { SkeletonModule } from 'primeng/skeleton';
import { of } from 'rxjs/internal/observable/of';
import { Subject } from 'rxjs/internal/Subject';
import { catchError, switchMap, takeUntil, tap } from 'rxjs/operators';
import { AuthService } from 'src/app/services/auth.service';
import { RoleService } from 'src/app/services/role.service';
import { DialogResult } from 'src/app/workflow/models/dialog-result.model';
import { TeamsMeetingPreview } from '../../teams-meeting-preview/teams-meeting-preview';
import { DividerModule } from 'primeng/divider';
import { UserExamRecordService } from 'src/app/services/user-exam-record.service';
import { UserExamRecord } from 'src/app/models/userExamRecord.model';
import { ExamRecordDialogComponent } from '../exam-record-dialog/exam-record-dialog.component';
import { UserAppIdService } from 'src/app/services/user-app-id.service';
import { Observable } from 'rxjs';

declare var Office: any;

// Backend: 6 Ziffern, jeweils 1–9 (keine 0)
const APP_ID_PATTERN = /^[1-9]{6}$/;

@Component({
  selector: 'app-create-user-dialog',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    ButtonModule,
    InputTextModule,
    DropdownModule,
    MultiSelectModule,
    InputTextareaModule,
    RadioButtonModule,
    CalendarModule,
    FloatLabelModule,
    CheckboxModule,
    DialogModule,
    EditorModule,
    ActivityPanelSmallComponent,
    TooltipModule,
    ActivityTypeComponent,
    SkeletonModule,
    // Zyklus-Kette DialogManagerService.
    forwardRef(() => TeamsMeetingPreview),
    DividerModule,
    ExamRecordDialogComponent,
  ],
  templateUrl: './create-user-dialog.component.html',
  styleUrl: './create-user-dialog.component.scss',
})
export class CreateUserDialogComponent implements OnInit, OnDestroy {
  public showAllRoles = false;
  public preselectedRole: number | null = null;
  public selectedUser: user;

  richTextEnabled = false;
  isEditing = false;
  isSubmitting = false;

  accessLevel = environment.accessLevel;
  permissions: string[];

  courses: Course[] = [];
  sponsors: Sponsor[] = [];

  newUserForm: FormGroup;

  leadStatus = LEAD_STATUS;
  careerInterests = CAREERS;
  genders = GENDERS;
  roles: Role[];
  educationLevels = EDUCATION_LEVELS;

  examTypes = EXAM_TYPES;
  examRecords: UserExamRecord[];
  isExamDialog = false;

  loading: boolean = false;

  /** Gespeicherte App-ID, um nur bei Änderung zu speichern */
  private savedAppId: number | null = null;

  /** Gesamtstunden vor Ort (Id_App); null bis geladen, nur bei bestehendem User */
  onSiteHours: number | null = null;

  /** Destroys subscription on component destroy */
  private destroy$: Subject<boolean> = new Subject();

  constructor(
    private sponsorService: SponsorService,
    private userService: UserService,
    private notificationService: NotificationService,
    private passwordService: PasswordService,
    private courseService: CourseService,
    private formBuilder: FormBuilder,
    private dialogManager: DialogManagerService,
    private dialogConfig: DynamicDialogConfig,
    private dateTimeService: DateTimeService,
    private roleService: RoleService,
    private auth: AuthService,
    private examRecordService: UserExamRecordService,
    private userAppIdService: UserAppIdService,
  ) {}

  ngOnInit(): void {
    this.loadSponsors();
    this.loadCourses();

    this.showAllRoles = this.dialogConfig.data?.showAllRoles ?? false;

    const incomingRole = this.dialogConfig.data?.preselectedRole;
    this.preselectedRole =
      incomingRole && incomingRole > 0 ? incomingRole : null;
    this.selectedUser = this.dialogConfig.data.selectedUser;
    this.loadRoles();
    this.enableRichTextField();
    this.initializeForm();
    this.loadUserDetails();
    this.loadExamRecords();
    this.loadAppId();
    this.loadOnSiteHours();

    this.permissions = this.auth.getUserPermissions();
  }

  ngOnDestroy(): void {
    // Emit a value to complete any active subscriptions
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  get canWriteUsers(): boolean {
    return this.permissions.includes('Users.Write');
  }

  get showExamResults(): boolean {
    return !this.isEditing && this.isUserSelected && this.accessLevel > 1;
  }

  get firstCourse(): Course | null {
    return this.selectedUser?.courses[0];
  }

  //#region Data loading

  loadUserDetails(): void {
    if (!this.isUserSelected) {
      return;
    }
    this.loading = true;
    this.userService
      .getUserDetails(this.selectedUser.userId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (detailedUser) => {
          detailedUser.activities = (detailedUser.activities ?? []).sort(
            (a, b) =>
              new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime(),
          );
          this.selectedUser = detailedUser;
          this.loading = false;
        },
        error: (error) => {
          this.notificationService.error(
            `Aktivitäten des Nutzers konnten nicht geladen werden.`,
            error,
          );
          this.loading = false;
        },
      });
  }

  loadCourses(): void {
    this.courseService
      .getCourseObservable()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data) => {
          this.courses = data.sort((a, b) =>
            a.courseName.localeCompare(b.courseName),
          );
        },
        error: () => console.error('Kurse nicht geladen!'),
      });
  }

  loadSponsors(): void {
    this.sponsorService
      .getSponsorObservable()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data) => {
          this.sponsors = data.sort((a, b) =>
            a.sponsorName.localeCompare(b.sponsorName),
          );
        },
        error: () => console.error('Kostenträger nicht geladen!'),
      });
  }

  loadRoles(): void {
    if (this.showAllRoles) {
      this.roleService
        .getRoles()
        .pipe(takeUntil(this.destroy$))
        .subscribe((data: Role[]) => {
          this.roles = data;
        });
    } else {
      this.roleService
        .getParticipantRoles()
        .pipe(takeUntil(this.destroy$))
        .subscribe((data: Role[]) => {
          this.roles = data;
        });
    }
  }

  loadExamRecords(): void {
    if (this.selectedUser?.userId) {
      this.examRecordService
        .getExamRecordsByUserId(this.selectedUser.userId)
        .subscribe((data: UserExamRecord[]) => {
          this.examRecords = data;
        });
    }
  }

  loadAppId(): void {
    if (!this.selectedUser?.userId) {
      return;
    }
    this.userAppIdService
      .getAppId(this.selectedUser.userId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (appId) => {
          this.savedAppId = appId;
          this.newUserForm.patchValue({ appId: appId?.toString() ?? '' });
        },
        error: (error) =>
          this.notificationService.error(
            'App-ID konnte nicht geladen werden.',
            error,
          ),
      });
  }

  loadOnSiteHours(): void {
    if (!this.selectedUser?.userId) {
      return;
    }
    this.userAppIdService
      .getOnSiteHours(this.selectedUser.userId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (hours) => (this.onSiteHours = hours),
        error: (error) =>
          this.notificationService.error(
            'Stunden vor Ort konnten nicht geladen werden.',
            error,
          ),
      });
  }

  //#endregion

  //#region Helper functions

  closeDialog(result?: DialogResult): void {
    this.newUserForm.reset({
      roleId: this.preselectedRole ? [this.preselectedRole] : [],
    });

    this.dialogManager.closeUserDialog(result);
  }

  get isUserSelected() {
    return this.selectedUser !== null && this.selectedUser !== undefined;
  }

  get roleId(): number[] {
    return this.newUserForm.controls['roleId'].value ?? [];
  }

  get islead(): boolean {
    return this.preselectedRole === null;
  }

  get isParticipant(): boolean {
    if (!this.roles) {
      return false;
    }
    const selectedRoles = this.roles.filter((r) =>
      this.roleId.includes(r.roleId),
    );
    return selectedRoles.some((r) => r.isParticipant);
  }

  enableRichTextField(): void {
    this.richTextEnabled = true;
  }

  disableRichTextField(): void {
    this.richTextEnabled = false;
  }

  getRoleObject(): Role[] {
    if (!this.roles || this.roles.length === 0) return [];

    const ids = this.islead ? [1] : (this.roleId ?? []);
    if (!ids || ids.length === 0) return [];

    return ids
      .map((id) => this.roles.find((r) => r.roleId === id))
      .filter((r): r is Role => !!r)
      .map(
        (r) =>
          ({
            roleId: r.roleId,
            roleName: r.roleName,
            permissions: [],
            isParticipant: r.isParticipant ?? false,
          }) as Role,
      );
  }

  buildUserObject(): user {
    const newResidence: Residence = {
      street: this.newUserForm.controls['street'].value,
      city: this.newUserForm.controls['city'].value,
      postalCode: this.newUserForm.controls['postalCode'].value,
      phoneNumber: this.newUserForm.controls['phoneNumber'].value,
    };

    const selectedSponsor: Sponsor = this.newUserForm.controls['sponsor'].value;

    const selectedRoles = this.getRoleObject().map((r) => ({
      ...r,
      permissions: (r as any).permissions ?? [],
    })) as Role[];

    console.log('Selected Roles:', selectedRoles);

    const newUser: user = {
      password: this.passwordService.createRandomPassword(12),

      roles: selectedRoles,
      residences: [newResidence],
      courses: this.newUserForm.controls['selectedCourses'].value,
      sponsors: selectedSponsor ? [selectedSponsor] : [],

      firstname: this.newUserForm.controls['firstname'].value,
      lastname: this.newUserForm.controls['lastname'].value,
      fullName:
        this.newUserForm.controls['firstname'].value +
        ' ' +
        this.newUserForm.controls['lastname'].value,
      email: this.newUserForm.controls['email'].value,
      internEmail: this.newUserForm.controls['internEmail'].value,
      gender: this.newUserForm.controls['gender'].value,
      isActive: this.newUserForm.controls['isActive'].value,
      isLead: this.islead,

      extraUserInfo: {
        contactDate: this.dateTimeService.convertDateToString(
          this.newUserForm.controls['contactDate'].value,
        ),
        customerId: this.newUserForm.controls['customerId'].value,
        clerkLastname: this.newUserForm.controls['clerkLastname'].value,
        clerkFirstname: this.newUserForm.controls['clerkFirstname'].value,
        clerkEmail: this.newUserForm.controls['clerkEmail'].value,
        clerkPhone: this.newUserForm.controls['clerkPhone'].value,
        birthDate: this.dateTimeService.convertDateToString(
          this.newUserForm.controls['birthDate'].value,
        ),
        birthPlace: this.newUserForm.controls['birthPlace'].value,
        financed: this.islead
          ? this.newUserForm.controls['financed'].value
          : null,
        careerInterest: this.islead
          ? this.newUserForm.controls['careerInterest'].value
          : null,
        educationLevel: this.newUserForm.controls['educationLevel'].value,
        leadStatus: this.islead
          ? this.newUserForm.controls['leadState'].value
          : null,
        misc: this.newUserForm.controls['misc'].value,
      },
    };

    return newUser;
  }

  //#endregion

  //#region Form initialization

  initializeForm() {
    if (this.isUserSelected) {
      this.setUpdateForm();
    } else {
      this.setCreationForm();
    }
  }

  setCreationForm() {
    this.newUserForm = this.formBuilder.group({
      roleId: [this.preselectedRole ? [this.preselectedRole] : []],
      gender: [''],
      contactDate: [this.dateTimeService.getCurrentDate()],
      firstname: ['', Validators.required],
      lastname: ['', Validators.required],
      email: [''],
      internEmail: [''],
      street: [''],
      postalCode: [''],
      city: [''],
      phoneNumber: [''],
      sponsor: [null],
      financed: [false],
      careerInterest: [''],
      educationLevel: [''],
      birthDate: [''],
      birthPlace: [''],
      customerId: [''],
      clerkFirstname: [''],
      clerkLastname: [''],
      clerkEmail: [''],
      clerkPhone: [''],
      leadState: [''],
      misc: [''],
      selectedCourses: [[]],
      isActive: [true],
    });
  }

  setUpdateForm() {
    const user = this.selectedUser;
    const extra = user.extraUserInfo;
    const residence =
      user.residences && user.residences.length > 0
        ? user.residences[0]
        : undefined;
    const courses: Course[] = user.courses ?? [];
    const sponsor =
      user.sponsors && user.sponsors.length > 0 ? user.sponsors[0] : undefined;

    this.newUserForm = this.formBuilder.group({
      roleId: [
        user.roles && user.roles.length > 0
          ? user.roles.map((r) => r.roleId)
          : this.preselectedRole
            ? [this.preselectedRole]
            : [],
        Validators.required,
      ],
      appId: ['', Validators.pattern(APP_ID_PATTERN)],

      gender: [user.gender],
      contactDate: [
        this.dateTimeService.convertStringToDate(extra?.contactDate),
      ],
      firstname: [user.firstname, Validators.required],
      lastname: [user.lastname, Validators.required],
      email: [user.email],
      internEmail: [user.internEmail],
      street: [residence?.street ?? ''],
      postalCode: [residence?.postalCode ?? ''],
      city: [residence?.city ?? ''],
      phoneNumber: [residence?.phoneNumber ?? ''],
      sponsor: [sponsor],
      financed: [extra?.financed],
      careerInterest: [extra?.careerInterest],
      educationLevel: [extra?.educationLevel],
      birthDate: [this.dateTimeService.convertStringToDate(extra?.birthDate)],
      birthPlace: [extra?.birthPlace],
      customerId: [extra?.customerId],
      clerkFirstname: [extra?.clerkFirstname],
      clerkLastname: [extra?.clerkLastname],
      clerkEmail: [extra?.clerkEmail],
      clerkPhone: [extra?.clerkPhone],
      leadState: [extra?.leadStatus],
      misc: [extra?.misc],
      selectedCourses: [courses],
      isActive: [user.isActive],
    });

    if (this.isUserSelected) {
      this.disableEdit();
    }
  }

  //#endregion

  //#region App-ID

  /** PUT bei neuem Wert, DELETE bei geleertem Feld, sonst nichts */
  private saveAppId(userId: number): Observable<unknown> {
    const value: string = this.newUserForm.controls['appId'].value ?? '';
    const appId = value.trim() ? Number(value) : null;
    if (appId === this.savedAppId) {
      return of(null);
    }
    const request$: Observable<unknown> =
      appId === null
        ? this.userAppIdService.deleteAppId(userId)
        : this.userAppIdService.setAppId(userId, appId);
    return request$.pipe(tap(() => (this.savedAppId = appId)));
  }

  //#endregion

  //#region Create User


  saveCreatedUser(openConsultation: boolean): void {
    if (!this.roles || this.roles.length === 0) {
      this.notificationService.error(
        'Rollen wurden noch nicht geladen. Bitte kurz erneut versuchen.',
      );
      this.isSubmitting = false;
      return;
    }

    const newUser = this.buildUserObject();
    if (!newUser.roles || newUser.roles.length === 0) {
      this.notificationService.error(
        'Keine Rolle ausgewählt. Bitte eine Rolle auswählen.',
      );
      this.isSubmitting = false;
      return;
    }

    this.isSubmitting = true;

    if (this.newUserForm.invalid) {
      this.newUserForm.markAllAsTouched();
      this.notificationService.error(
        'Es fehlen Angaben. Bitte füllen Sie das Formular vollständig aus.',
      );
      this.isSubmitting = false;
      return;
    }

    const hasMissingPermissions = newUser.roles.some(
      (r) => !(r as any).permissions,
    );
    if (hasMissingPermissions) {
      this.notificationService.error(
        'Keine Berechtigungsinformationen für die ausgewählte Rolle. Überprüfen Sie die Antwort der Rollen-API (Berechtigungen sollten vorhanden sein).',
      );
      this.isSubmitting = false;
      return;
    }

    this.userService
      .createUser(newUser)
      .pipe(
        takeUntil(this.destroy$),
        tap((user: user) => {
          if (openConsultation)
            this.dialogManager.openConsultationDialog({ user });
          this.handleUserCreated(user);
        }),
        catchError((error: HttpErrorResponse) => {
          this.handleUserCreationError(error);
          return of(null);
        }),
      )
      .subscribe(() => {
        this.isSubmitting = false;
      });
  }

  // Bewusst keine Einladungs-Mail: den Portal-Zugang vergeben wir gesondert
  handleUserCreated(user: user): void {
    this.notificationService.success(
      user.isLead
        ? `Interessent:in ${user.fullName} erfolgreich erstellt.`
        : `Benutzer:in ${user.email} erfolgreich erstellt.`,
      user,
    );

    try {
      Office.context.ui.messageParent('TN_CREATED');
    } catch {}

    this.isSubmitting = false;
    this.closeDialog();
  }

  handleUserCreationError(e: HttpErrorResponse): void {
    if (e.status === 409) {
      this.notificationService.error(`${this.conflictReason(e)} Nutzer:in NICHT erstellt.`, e);
    } else {
      this.notificationService.error(`Fehler: Nutzer:in NICHT erstellt.`, e);
    }
  }

  /** 409: E-Mail oder interne E-Mail schon vergeben, der Grund steht in der Antwort */
  private conflictReason(e: HttpErrorResponse): string {
    return e.error?.message ?? 'E-Mail-Adresse existiert schon.';
  }

  //#region Update User

  updateUser() {
    this.isSubmitting = true;
    this.disableEdit();
    const updatedUser = this.buildUserObject();
    updatedUser.password = undefined;
    updatedUser.userId = this.selectedUser.userId;

    // App-ID zuerst: bei 409 bleibt alles ungespeichert
    this.saveAppId(this.selectedUser.userId!)
      .pipe(
        switchMap(() => this.userService.updateCompleteUser(updatedUser)),
        takeUntil(this.destroy$),
      )
      .subscribe({
        next: (response) => {
          this.notificationService.success(
            `Nutzer:in ${updatedUser.fullName} erfolgreich geändert`,
            response,
          );
          this.isSubmitting = false;
          this.closeDialog({ success: true });
          this.userService.userUpdated$.next();
        },
        error: (error: HttpErrorResponse) => {
          this.isSubmitting = false;
          // Dialog offen lassen, damit die Adresse korrigiert werden kann
          if (error.status === 409) {
            this.notificationService.error(
              `${this.conflictReason(error)} Nutzer:in NICHT geändert.`,
              error,
            );
            this.enableEdit();
            return;
          }
          this.notificationService.error(
            `Nutzer:in ${updatedUser.fullName} NICHT geändert`,
            error,
          );
          this.closeDialog();
        },
      });
  }

  enableEdit() {
    this.isEditing = true;
    this.newUserForm.enable();
    this.enableRichTextField();
  }

  disableEdit() {
    this.isEditing = false;
    this.newUserForm.disable();
    this.disableRichTextField();
  }

  //#region Aktivität

  openActivityDetails(activityId: number): void {
    this.dialogManager.openActivityDetails({ activityId });
  }

  //#endregion

  //#region SPONSOR

  showAddSponsorDialog(): void {
    this.dialogManager.openSponsorDialog();
  }

  //#endregion

  //#region KURS ANLEGEN

  showAddCourseDialog(): void {
    this.dialogManager.openCourseDialog();
  }

  //#endregion

  // #region Teams Meeting
  get isRoleLecturer(): boolean {
    if (!this.selectedUser.roles?.length || !environment.roleNamesForLecturer) {
      return false;
    }
    return environment.roleNamesForLecturer.some((roleName) =>
      this.selectedUser.roles.some((r) => r.roleName === roleName),
    );
  }

  get canWriteMeeting(): boolean {
    return this.auth.getUserPermissions().includes('Meeting.Write');
  }
  //#endregion

  // #region Exam Type
  getExamTypeLabel(value: string) {
    return this.examTypes.find((e) => e.value == value)?.label;
  }

  showExamRecordDialog(): void {
    this.isExamDialog = true;
  }

  handleExamRecordDialogClose(result: UserExamRecord | null): void {
    this.isExamDialog = false;
    if (result) {
      this.examRecordService.createExamRecord(result).subscribe(() => {
        this.loadExamRecords();
        this.notificationService.success(
          'Prüfungsergebnis erfolgreich gespeichert.',
          result,
        );
      });
    }
  }
  //#endregion
}
